import * as React from 'react';

import DocumentHeader from '../../../components/DocumentHeader';
import DocumentWrapper from '../../../components/DocumentWrapper';
import FormEditingHandler from '../../../containers/FormEditingHandler';

import DiagnosticSurveyForm from './Form';

import { TDocument } from '../utils';
import NPIHistory from './HistoryRowData/NPI';
import FBIMODHistory from './HistoryRowData/FBIMOD';
import BNSQHistory from './HistoryRowData/BNSQ';
import { FormContextProvider } from '../../../containers/FormContextHandler';
import { InformationButton } from '../components';
import { npiFormInformation } from './Form/NPI/definitions';
import ADLHistory from './HistoryRowData/ADL';
import IADLHistory from './HistoryRowData/IADL';
import { path } from 'Utility/ramdaReplacement';
import { useAppSelector } from 'Store/index';
import { actions as myAppActions } from 'Store/myapp/actions';
import { useDispatch } from 'react-redux';
import StandAloneFormEditingHandler from 'Containers/StandAloneFormEditingHandler';
import QuestionAnswerPair from 'Components/_NewElements/QuestionAnswerPair';
import { useIntl } from 'react-intl';
import { surveyCreator, surveyModifyer } from 'Routes/MyService/Document/HistoryRowData/SurveysHistory';
import { isByInformant } from 'Utility/documentHandling';
import { useLocation, useNavigate } from 'react-router';

const docType = (documents: Array<TDocument>, editing: string, view?: IView): string | undefined =>
  documents && editing
    ? path(
        ['_type'],
        documents.find((d: TDocument) => d._id === editing),
      ) || undefined
    : documents && view?.viewing
      ? path(
          ['_type'],
          documents.find((d: TDocument) => d._id === view?.viewing, documents),
        ) || undefined
      : undefined;

const DiagnosticSurveyHistory = (
  documents: Array<TDocument>,
  startEdit: (document: TAnyObject, name?: string) => (e: React.MouseEvent<Element, MouseEvent>) => void,
  handleMyDelete: (myDocId: string, docType: string) => Promise<void>,
  view?: IView,
): React.JSX.Element => {
  const NPIDocs = documents && documents.filter(d => d._type === 'npi');
  const FBIMODDocs = documents && documents.filter(d => d._type === 'fbimod');
  const BNSQDocs = documents && documents.filter(d => d._type === 'bnsq');
  const ADLDocs = documents && documents.filter(d => d._type === 'adl');
  const IADLDocs = documents && documents.filter(d => d._type === 'iadl');
  return (
    <React.Fragment>
      <NPIHistory documents={NPIDocs as INPI[]} startEdit={startEdit} view={view} />

      <FBIMODHistory
        documents={FBIMODDocs as IFBIMOD[]}
        startEdit={startEdit}
        view={view}
        handleMyDelete={handleMyDelete}
      />

      <BNSQHistory documents={BNSQDocs as IBNSQ[]} startEdit={startEdit} view={view} />

      <ADLHistory documents={ADLDocs as IADL[]} startEdit={startEdit} view={view} />

      <IADLHistory documents={IADLDocs as IIADL[]} startEdit={startEdit} view={view} handleMyDelete={handleMyDelete} />
    </React.Fragment>
  );
};

const DiagnosticSurvey = ({ documents }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const users = useAppSelector(state => state.session.orgUsers);
  const mysqUserId = useAppSelector((s: IState) => s.myapp.mysqUserId) || '';

  const surveyDocs = documents.filter(d => !['diagnosis', 'mmse'].includes(d._type));

  const informants = useAppSelector((state: IState) => state.patient.informants) ?? [];

  const myFbiModDocs =
    useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments)?.filter(d => d._type === 'fbimod') ?? [];
  const myIadlDocs =
    useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments)?.filter(d => d._type === 'iadl') ?? [];

  // My document handling
  const [myFormOpen, setMyFormOpen] = React.useState<
    | {
        doc: IFBIMOD | IIADL;
        type: 'edit' | 'view';
      }
    | undefined
  >(undefined);

  const [tempMyForm, setTempMyForm] = React.useState<(IFBIMOD | IIADL) | undefined>(undefined);
  const [saving, setSaving] = React.useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Intercept navigation from SurveysHistory to open informant docs in StandAloneFormEditingHandler
  React.useEffect(() => {
    const state = location.state as
      | { myFormDocType?: string; initData?: IFBIMOD | IIADL; type: 'view' | 'edit' }
      | undefined;
    if (
      state?.myFormDocType &&
      state?.initData &&
      (state.myFormDocType === 'fbimod' || state.myFormDocType === 'iadl')
    ) {
      setMyFormOpen({
        doc: state.initData,
        type: state.type,
      });
    }
  }, [location.state]);

  const saveMyForm = async () => {
    setSaving(true);
    await myAppActions.putNewCommit(myFormOpen?.doc._type ?? '', myFormOpen?.doc._id ?? '', tempMyForm, dispatch);
    setSaving(false);
    setTempMyForm(undefined);
  };

  const handleMyFormChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    const value = values[name];
    if (name && (value || value === 0)) {
      setTempMyForm(prev => ({
        ...(prev as IFBIMOD | IIADL),
        [name]: value,
      }));
    }
  };

  const handleMyFormDelete = async (myDocId: string, docType: string) => {
    await myAppActions.deleteDocument(docType, myDocId, dispatch);
  };

  if (myFormOpen) {
    const survEditor = surveyModifyer(myFormOpen.doc, mysqUserId, users ?? [], [], informants, fm);
    return (
      <StandAloneFormEditingHandler
        currentPageName="diagnosticSurvey"
        headerLocaleId={
          myFormOpen.doc._type === 'fbimod' ? 'diagnosticSurvey.fbimod.title' : 'diagnosticSurvey.iadl.title'
        }
        headerInfoText={
          myFormOpen.doc._type === 'fbimod' ? 'diagnosticSurvey.fbimod.info' : 'diagnosticSurvey.iadl.info'
        }
        loading={saving}
        cancelEditingAction={() => {
          if (location?.state && 'myFormDocType' in location.state) {
            navigate({ pathname: '/myService' });
          }
          setMyFormOpen(undefined);
          setTempMyForm(undefined);
        }}
        viewing={myFormOpen?.type === 'view'}
        endViewingAction={() => {
          if (location?.state && 'myFormDocType' in location.state) {
            navigate({ pathname: '/myService' });
          }
          setMyFormOpen(undefined);
        }}
        saveAction={async () => {
          setSaving(true);
          await saveMyForm();
          setMyFormOpen(undefined);
          setTempMyForm(undefined);
          setSaving(false);
        }}
      >
        <FormContextProvider
          context={{
            formData: { document: tempMyForm ?? myFormOpen.doc, onChange: handleMyFormChange },
            view:
              myFormOpen?.type === 'view'
                ? { viewing: myFormOpen.doc._id, document: myFormOpen.doc, startView: () => () => null }
                : undefined,
          }}
        >
          <>
            <div style={{ marginBottom: '4rem' }}>
              <QuestionAnswerPair
                question={fm('myService.reporter')}
                questionSize={5}
                answer={surveyCreator(myFormOpen.doc, mysqUserId, users ?? [], [], informants, fm)}
              />
              {survEditor && <QuestionAnswerPair question={fm('myService.edited')} answer={survEditor} />}
            </div>
            <DiagnosticSurveyForm document={myFormOpen.doc} />
          </>
        </FormContextProvider>
      </StandAloneFormEditingHandler>
    );
  }

  return (
    <FormEditingHandler name="diagnosticSurvey" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => {
        const startEditFunction = (document: TAnyObject, name?: string) =>
          isByInformant(document, informants)
            ? () => {
                setMyFormOpen({
                  doc: document as unknown as IFBIMOD | IIADL,
                  type: 'edit',
                });
                setTempMyForm(document as unknown as IFBIMOD | IIADL);
              }
            : startEdit(document, name);

        const startViewFunction = (document: TAnyObject) =>
          isByInformant(document, informants)
            ? () =>
                setMyFormOpen({
                  doc: document as unknown as IFBIMOD | IIADL,
                  type: 'view',
                })
            : view && document._id && typeof document._id === 'string'
              ? view.startView({ _id: document._id })
              : () => null;
        return (
          <DocumentWrapper>
            <DocumentHeader
              name={'diagnosticSurvey'}
              headerId={
                docType(surveyDocs, editing, view) === 'npi'
                  ? 'diagnosticSurvey.npi.title'
                  : docType(surveyDocs, editing, view) === 'fbimod'
                    ? 'diagnosticSurvey.fbimod.title'
                    : docType(surveyDocs, editing, view) === 'bnsq'
                      ? 'diagnosticSurvey.bnsq.title'
                      : docType(surveyDocs, editing, view) === 'adl'
                        ? 'diagnosticSurvey.adl.title'
                        : docType(surveyDocs, editing, view) === 'iadl'
                          ? 'diagnosticSurvey.iadl.title'
                          : 'diagnosticSurvey.title'
              }
              headerWidth={docType(surveyDocs, editing, view) === 'npi' ? 8 : undefined}
              info={
                docType(surveyDocs, editing, view) === 'npi' ? (
                  <InformationButton info={npiFormInformation.survey} size="3rem" />
                ) : undefined
              }
              infoText={
                docType(surveyDocs, editing, view) === 'npi'
                  ? undefined
                  : docType(surveyDocs, editing, view) === 'fbimod'
                    ? 'diagnosticSurvey.fbimod.info'
                    : docType(surveyDocs, editing, view) === 'bnsq'
                      ? 'diagnosticSurvey.bnsq.info'
                      : docType(surveyDocs, editing, view) === 'adl'
                        ? 'diagnosticSurvey.adl.info'
                        : docType(surveyDocs, editing, view) === 'iadl'
                          ? 'diagnosticSurvey.iadl.info'
                          : undefined
              }
              editing={editing}
            />
            {editing || view?.viewing ? (
              <FormContextProvider context={{ formData, editing, view, documents }}>
                <DiagnosticSurveyForm
                  document={surveyDocs && surveyDocs.find(d => d._id === editing || d._id === view?.viewing)}
                />
              </FormContextProvider>
            ) : null}

            {!editing && !view?.viewing
              ? DiagnosticSurveyHistory(
                  [...surveyDocs, ...myFbiModDocs, ...myIadlDocs],
                  startEditFunction,
                  handleMyFormDelete,
                  view ? { ...view, startView: startViewFunction } : undefined,
                )
              : undefined}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<TDocument | IDiagnosis | IMMSE>;
}

export default DiagnosticSurvey;
